import React from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Building2, MapPin, Home, Bell, Upload, FileText,
  Heart, Bookmark, Gavel, ShieldCheck, Ticket, LogIn as LogInIcon,
  Megaphone, Search, BarChart3, Sparkles, Crown, ArrowRight,
} from 'lucide-react';
import { LegalFooterLinks } from '../components/legal/LegalDocument';

/**
 * Public feature list page — pitches what PropScope does to prospects.
 * Linked from marketing copy and reachable at /features.
 */

const SECTIONS = [
  {
    title: 'Search & Discovery',
    blurb: 'Find investment-grade UK property fast.',
    items: [
      {
        icon: Building2, label: 'Property Listings',
        who: 'Everyone', where: 'Sidebar → Properties',
        what: 'Every active property we track — auctions, estate-agent uploads, deal sources, scraped portals — with our investment score, gross yield, price-vs-market deviation, and AI verdict.',
      },
      {
        icon: Search, label: 'Filtering & Personalised Scoring',
        who: 'Everyone', where: 'Properties page',
        what: 'Filter by postcode, radius around a postcode, property type, beds, price band, minimum score, minimum yield, source. Adjust the scoring sliders to weight what you care about (price, yield, area trend, HMO potential) — properties re-sort live.',
      },
      {
        icon: Bookmark, label: 'Saved Search Profiles',
        who: 'Logged-in users', where: 'Properties page',
        what: 'Save your filters + slider weights as named profiles ("Manchester core", "Leeds suburban"). Switch between them with one click. Different operators in different areas can each have their own setup.',
      },
      {
        icon: Heart, label: 'Watchlist',
        who: 'Logged-in users', where: 'Heart icon on every property card',
        what: 'Click the heart on any property to add it to your watchlist. The Dashboard has a "My Watchlist" toggle that scopes every stat and chart to just your saved properties.',
      },
      {
        icon: Gavel, label: 'Auction Houses Directory',
        who: 'Everyone', where: 'Sidebar → Auction Houses',
        what: 'Browse properties grouped by auction house — Allsop, Acuitus, GoTo Properties, Bamboo, Clive Emson, Future Property Auctions, Agents Property Auction, and others. Click a house to see all of their lots in one filtered view.',
      },
    ],
  },
  {
    title: 'Investment Intelligence',
    blurb: 'Per-property data the listings sites do not show.',
    items: [
      {
        icon: BarChart3, label: 'Investment Scoring',
        who: 'Everyone', where: 'Every property card and detail page',
        what: 'A 0-100 score combining price deviation from market value, gross rental yield, area price-trend over 10 years, and HMO opportunity. Lender-aware penalties for poor EPC and flood risk.',
      },
      {
        icon: Sparkles, label: 'AI Verdict & Analysis',
        who: 'Everyone', where: 'Property detail page',
        what: 'Each property gets a Strong Buy / Buy / Hold / Avoid verdict from our AI investment analysis system. Highlights the deal\'s strengths, the watch-outs, and a one-paragraph summary you can paste into a partner email.',
      },
      {
        icon: MapPin, label: 'Neighbourhood Reports',
        who: 'Everyone', where: 'Sidebar → Area Lookup, or "View area" on any property',
        what: 'Crime stats, schools, transport, broadband, conservation areas, flood risk, listed-building proximity, IMD deprivation rank, sales history, comparable prices, rental market, and an interactive map.',
      },
      {
        icon: FileText, label: 'Sales History & Comparables',
        who: 'Everyone', where: 'Property detail page',
        what: '10-year Land Registry sales for the property and a comparables table for similar nearby sales. Histogram of price distribution by property type in the area.',
      },
    ],
  },
  {
    title: 'Your Portfolio',
    blurb: 'Manage what you actually own.',
    items: [
      {
        icon: Home, label: 'My Properties',
        who: 'Logged-in users', where: 'Sidebar → My Properties',
        what: 'Add the properties you own or manage. Each card shows the latest indicative valuation and lets you re-run the calculator with up-to-date condition info.',
      },
      {
        icon: BarChart3, label: 'AVM-Enhanced Valuation Wizard',
        who: 'Logged-in users', where: 'My Properties → "Get an Indicative Value"',
        what: 'Five-step questionnaire — basics, features (parking, garden, EPC, heating, multi-select extensions), condition scoring (per-room age + emoji modifier), seller situation, photos. Returns a low/mid/high indicative value range with a transparent breakdown.',
      },
      {
        icon: FileText, label: 'Document Vault',
        who: 'Logged-in users', where: 'My Properties → "Documents" panel',
        what: 'Upload mortgage offers, ASTs, EPCs, insurance, land-registry, loan paperwork. Each property has its own folder. Files are private and encrypted. 25 MB per file.',
      },
      {
        icon: Bell, label: 'Property Alerts',
        who: 'Logged-in users', where: 'Sidebar → Alerts',
        what: 'Set match criteria; get email digests of new properties matching your scoring preferences. Daily or immediate cadence.',
      },
    ],
  },
  {
    title: 'For Auction Houses & Estate Agents',
    blurb: '£49.99/month basic — get your stock in front of investors.',
    items: [
      {
        icon: Upload, label: 'Listing Uploader',
        who: 'Auction House / Estate Agent tier', where: 'Sidebar → Upload Listings',
        what: 'Three tabs: add a single listing manually, bulk-upload via CSV with a downloadable template, view all your previous uploads. Auction tab captures guide price + auction date; agent tab captures asking price + EPC + status.',
      },
      {
        icon: Crown, label: 'Pro Tier — Full Data Access',
        who: 'Auction House / Estate Agent Pro (£99.99/mo)', where: 'Account → Plan',
        what: 'Same upload tools as Basic, plus full investment data on every property in the system regardless of source. Use it for market intelligence and deal scouting.',
      },
    ],
  },
  {
    title: 'Account & Billing',
    blurb: 'Easy in, easy out.',
    items: [
      {
        icon: Crown, label: 'Tiered Subscriptions',
        who: 'Everyone', where: 'Account page → Plan card',
        what: 'Investor £99.99/mo. Auction House / Estate Agent Basic £49.99/mo. Pro upgrades £99.99/mo. Stripe-managed; cancel any time at end of period.',
      },
      {
        icon: Ticket, label: 'Promo / Demo Codes',
        who: 'Everyone', where: 'Account page → "Redeem a Promo Code"',
        what: 'If you have a demo code, paste it on /account to start a free trial — 7, 14, 28 days or longer. Codes are single-use per account.',
      },
      {
        icon: FileText, label: 'Invoices & Cancellation',
        who: 'Logged-in users', where: 'Account page → "Invoices, Plan & Cancel"',
        what: 'Self-service portal hosted by Stripe — invoices, payment-method changes, plan switches, and end-of-period cancellation.',
      },
    ],
  },
  {
    title: 'Admin Tools',
    blurb: 'For PropScope staff. Granted via the platform-admin flag.',
    items: [
      {
        icon: ShieldCheck, label: 'User Management',
        who: 'Admins only', where: '/admin/users',
        what: 'Search every user; toggle the platform-admin flag on or off. Layered on top of any tier — an auction-house user can also be admin without losing their tier-specific UI.',
      },
      {
        icon: Ticket, label: 'Promo Code Management',
        who: 'Admins only', where: '/admin/promo-codes',
        what: 'Create time-bounded or lifetime-free codes. Custom code text optional; system auto-generates high-entropy tokens by default. See redemptions, deactivate, extend trials.',
      },
      {
        icon: LogInIcon, label: 'Login Log',
        who: 'Admins only', where: '/admin/login-log',
        what: 'Chronological list of every successful login with name, email, IP, and user-agent. Free-text filter at top.',
      },
      {
        icon: Megaphone, label: 'Ad Bar Management',
        who: 'Admins only', where: '/admin/ads',
        what: 'Approve advertiser submissions, edit the live ad slot at the bottom of every page, fall back to the PropScope house-ad when no advertiser is configured.',
      },
    ],
  },
];


export default function Features() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      {/* Header */}
      <header className="border-b border-slate-800 px-6 py-8">
        <div className="max-w-5xl mx-auto flex items-center gap-6">
          <img src="/propscope-logo.png" alt="PropScope" className="h-16 w-auto" />
          <div>
            <h1 className="text-3xl font-bold text-white">PropScope</h1>
            <p className="text-slate-400 text-sm tracking-wide mt-1">Analyse Smarter. Invest Better.</p>
          </div>
          <div className="ml-auto flex gap-2">
            <Link to="/login" className="text-sm bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg">
              Sign in
            </Link>
            <Link to="/register" className="text-sm bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2 rounded-lg inline-flex items-center gap-1">
              Create account <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* Intro */}
      <section className="px-6 pt-12 pb-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-semibold text-white">What PropScope does</h2>
          <p className="text-slate-400 mt-2 max-w-2xl">
            UK property investment intelligence. Pulls together auctions, estate-agent listings, scraped portals,
            and Land Registry data; layers on per-property scoring, AI verdicts, neighbourhood reports, and a
            personal portfolio toolkit. Below: every feature, who it's for, and where to find it.
          </p>
        </div>
      </section>

      {/* Sections */}
      <main className="px-6 pb-20">
        <div className="max-w-5xl mx-auto space-y-12">
          {SECTIONS.map(section => (
            <section key={section.title}>
              <div className="border-b border-slate-800 pb-2 mb-6">
                <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                <p className="text-slate-500 text-sm mt-1">{section.blurb}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.items.map(item => {
                  const Icon = item.icon;
                  return (
                    <article key={item.label} className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                          <Icon size={18} className="text-emerald-300" />
                        </div>
                        <h3 className="text-white font-semibold">{item.label}</h3>
                      </div>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.what}</p>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-slate-800/50 rounded-md px-2 py-1.5">
                          <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Who</span>
                          <span className="text-slate-300">{item.who}</span>
                        </div>
                        <div className="bg-slate-800/50 rounded-md px-2 py-1.5">
                          <span className="text-slate-500 block text-[10px] uppercase tracking-wide">Where to find it</span>
                          <span className="text-slate-300">{item.where}</span>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </main>

      {/* Footer CTA */}
      <footer className="border-t border-slate-800 py-10 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-slate-400 text-sm mb-4">Ready to take a look?</p>
          <div className="flex justify-center gap-3">
            <Link to="/register" className="bg-emerald-500 hover:bg-emerald-400 text-white px-5 py-2.5 rounded-lg font-semibold inline-flex items-center gap-1.5">
              Create your account <ArrowRight size={14} />
            </Link>
            <Link to="/login" className="bg-slate-800 hover:bg-slate-700 text-white px-5 py-2.5 rounded-lg">
              Sign in
            </Link>
          </div>
          <p className="text-slate-600 text-xs mt-6">PropScope — UK Property Investment Intelligence</p>
          <LegalFooterLinks className="mt-4" />
        </div>
      </footer>
    </div>
  );
}
